/**
 * Server-side audit exports (F25 / gmp-auditor F21).
 *
 * Every audit screen exports through the backend so the file covers the whole filtered set (not
 * just the loaded page), is properly CSV-escaped and carries a row count + SHA-256 footer
 * (`# rows=N`, `# sha256=...`) plus `X-Row-Count` / `X-Content-SHA256` headers.
 *
 * Endpoint (HYGIENE lane, djangoapp/urls_hygiene.py): GET /bp/audit/export/?section=<key>&<filters>
 * Sections: security-events | master-data-changes | data-integrity | permission-changes | reviews |
 *           change-history-batch | change-history-recipe | change-history-all | batch-audit | batch-audit-details
 *
 * TODO(QUALITY lane): when the certified `/bp/exports/...` endpoints land (views/SystemEvents/export_view.py),
 * point EXPORT_URL at them — the call sites only depend on this helper.
 */
import Axios from 'axios';
import { toast } from 'react-toastify';

const EXPORT_URL = '/bp/audit/export/';

/** Map of AuditLogsReport tab keys → export section keys. */
export const AUDIT_TAB_SECTIONS = {
  security: 'security-events',
  'master-data': 'master-data-changes',
  'data-integrity': 'data-integrity',
  permission: 'permission-changes',
  review: 'reviews',
};

const filenameFrom = (headers, fallback) => {
  const cd = headers?.['content-disposition'] || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return m ? m[1] : fallback;
};

/**
 * Download one audit section as CSV. Resolves to `{ rows, sha256 }` or `null` on failure.
 * Clean params (undefined / '' / null are dropped) are sent as query string.
 */
export async function downloadAuditExport(section, params = {}, { silent = false } = {}) {
  const query = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  try {
    const token = localStorage.getItem('Token');
    const res = await Axios.get(EXPORT_URL, {
      params: { section, ...query },
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    });
    const rows = Number(res.headers?.['x-row-count'] ?? NaN);
    const sha256 = res.headers?.['x-content-sha256'] || '';
    const name = filenameFrom(res.headers, `audit_${section}_${new Date().toISOString().slice(0, 10)}.csv`);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    if (!silent) {
      toast.success(
        Number.isFinite(rows)
          ? `Exported ${rows} row(s)${sha256 ? ` · SHA-256 ${sha256.slice(0, 12)}…` : ''}`
          : 'Export downloaded'
      );
    }
    return { rows, sha256, filename: name };
  } catch (err) {
    let msg = err?.response?.data?.message || err?.message || 'Export failed';
    // Blob error bodies need to be read back as text to surface the server message.
    if (err?.response?.data instanceof Blob) {
      try {
        const txt = await err.response.data.text();
        msg = JSON.parse(txt)?.message || msg;
      } catch (_e) {
        /* keep msg */
      }
    }
    if (!silent) toast.error(`Export failed: ${msg}`);
    return null;
  }
}
